import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Web as WebIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import BusinessAnalyzer from './BusinessAnalyzer';
import WebsiteAnalyzer from './WebsiteAnalyzer';
import ExportResults from './ExportResults';
import AuditGenerator from './AuditGenerator';
import ProspectionTemplates from './ProspectionTemplates';
import ProposalGenerator from './ProposalGenerator';
import ProspectionDashboard from './ProspectionDashboard';
import CanvaExport from './CanvaExport';
import CompetitiveAnalysis from './CompetitiveAnalysis';
import TemporalTracking from './TemporalTracking';
import CRMIntegration from './CRMIntegration';
import { businessFinder } from '../utils/businessFinder';
import apiClient from "../services/apiClient";

const ProspectionTool = () => {
  const [searchParams, setSearchParams] = useState({
    location: '',
    radius: 5,
    businessType: 'restaurant'
  });
  
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState("");

  const businessTypes = [
    { value: 'restaurant', label: 'Restaurants' },
    { value: 'avocat', label: 'Avocats' },
    { value: 'coiffeur', label: 'Coiffeurs' },
    { value: 'dentiste', label: 'Dentistes' },
    { value: 'plombier', label: 'Plombiers' },
    { value: 'electricien', label: 'Électriciens' },
    { value: 'garage', label: 'Garages' },
    { value: 'pharmacie', label: 'Pharmacies' },
    { value: 'boulangerie', label: 'Boulangeries' },
    { value: 'medecin', label: 'Médecins' }
  ];

  const handleSearch = async () => {
    if (!searchParams.location.trim()) {
      setError('Veuillez saisir une localisation');
      return;
    }

    setIsSearching(true);
    setError('');
    
    try {
      // Utilisation du service de recherche d'entreprises
      const searchResults = await businessFinder.searchBusinesses(searchParams);
      setResults(searchResults);
    } catch (err) {
      setError('Erreur lors de la recherche: ' + err.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleImportToProspects = async () => {
    setImporting(true);
    setImportMessage("");
    try {
      const data = await apiClient.adminProspectsImportSearch({
        location: searchParams.location,
        radius: Number(searchParams.radius),
        businessType: searchParams.businessType,
      });
      setImportMessage(`${data.imported || 0} prospect(s) qualifies et fusionnes dans le CRM.`);
    } catch (err) {
      setImportMessage(`Import impossible: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const getBusinessPotentialColor = (potential) => {
    switch (potential) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  const getBusinessPotentialLabel = (potential) => {
    switch (potential) {
      case 'high': return 'Potentiel Élevé';
      case 'medium': return 'Potentiel Moyen';
      case 'low': return 'Potentiel Faible';
      default: return 'Inconnu';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
        🔍 Outil de Prospection Web Local
      </Typography>
      
      <Typography variant="h6" gutterBottom align="center" color="text.secondary" sx={{ mb: 4 }}>
        Identifiez et analysez les sites web d'entreprises locales avec des problèmes techniques
      </Typography>

      {/* Formulaire de recherche */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Paramètres de recherche
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Localisation (ville, adresse, code postal)"
              value={searchParams.location}
              onChange={(e) => setSearchParams({...searchParams, location: e.target.value})}
              placeholder="Ex: Paris 75001, Lyon 69001..."
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type d'entreprise</InputLabel>
              <Select
                value={searchParams.businessType}
                label="Type d'entreprise"
                onChange={(e) => setSearchParams({...searchParams, businessType: e.target.value})}
              >
                {businessTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box>
              <Typography gutterBottom>
                Rayon de recherche: {searchParams.radius} km
              </Typography>
              <Slider
                value={searchParams.radius}
                onChange={(e, value) => setSearchParams({...searchParams, radius: value})}
                min={1}
                max={20}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={isSearching}
            sx={{ px: 4 }}
          >
            {isSearching ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Recherche en cours...
              </>
            ) : (
              'Lancer la recherche'
            )}
          </Button>
          <Button
            variant="outlined"
            sx={{ ml: 2 }}
            onClick={handleImportToProspects}
            disabled={isSearching || importing || !searchParams.location.trim()}
          >
            {importing ? "Qualification en cours..." : "Qualifier et ajouter au CRM"}
          </Button>
        </Box>
        {importMessage && (
          <Alert severity={importMessage.startsWith("Import impossible") ? "error" : "success"} sx={{ mt: 2 }}>
            {importMessage}
          </Alert>
        )}
      </Paper>

      {/* Affichage des erreurs */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tableau de bord */}
      <ProspectionDashboard results={results} />

      {/* Résultats */}
      {results.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Entreprises trouvées ({results.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setExportDialogOpen(true)}
            >
              Exporter les résultats
            </Button>
          </Box>
          
          {results.map((business) => (
            <Accordion key={business.id} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="h6">{business.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {business.address}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2">
                      📞 {business.phone}
                    </Typography>
                    <Typography variant="body2">
                      🌐 {business.website}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Chip
                      label={getBusinessPotentialLabel(business.businessPotential)}
                      color={getBusinessPotentialColor(business.businessPotential)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      {businessTypes.find(t => t.value === business.type)?.label}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ width: '100%' }}>
                  <WebsiteAnalyzer website={business.website} businessName={business.name} />
                  <AuditGenerator business={business} websiteAnalysis={business.websiteAnalysis} />
                  <ProspectionTemplates business={business} websiteAnalysis={business.websiteAnalysis} />
                  <ProposalGenerator business={business} websiteAnalysis={business.websiteAnalysis} />
                  <CanvaExport business={business} websiteAnalysis={business.websiteAnalysis} />
                  <CompetitiveAnalysis business={business} websiteAnalysis={business.websiteAnalysis} />
                  <TemporalTracking business={business} websiteAnalysis={business.websiteAnalysis} />
                  <CRMIntegration business={business} websiteAnalysis={business.websiteAnalysis} prospects={results} />
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Dialog d'export */}
      <ExportResults
        results={results}
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </Container>
  );
};

export default ProspectionTool;